import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Examination_navbar from "./Examination_navbar";
import { scrollToTop } from "../Shared/ScrollOnTop";

function ExaminationGuidelines() {
  useEffect(() => {
    document.title = "Examination Guidelines";
  }, []);

  return (
    <>
      <Examination_navbar />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/Dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/ExaminationSystem/ExaminationDashboard">
                      Examinations
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Guidelines
                  </li>
                </ol>
              </nav>
              <div className="page-title">Examination System Guidelines</div>
            </div>
            <div className="col-md-4">
              <div className="page-right-actions">
                <Link
                  to="/ExaminationSystem/ManageResultCards"
                  className="button button-white button-icon"
                  onClick={scrollToTop}
                >
                  Result Cards
                  <i className="ri-list-check"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <div className="container">
          <div className="card">
            <div className="card-body p-md-4">
              <h5>1. Exam Setup Flow</h5>
              <p>
                Set up examinations in this order before entering marks or
                publishing results:
              </p>
              <ol>
                <li>
                  <strong>Subject Types &amp; Subjects</strong> — define subject
                  categories and individual subjects.
                </li>
                <li>
                  <strong>Section Subjects</strong> — assign subjects to each
                  class section for the session.
                </li>
                <li>
                  <strong>Examinations</strong> — create exams with code, title,
                  dates, session, and exam type.
                </li>
                <li>
                  <strong>Manage Exam Classes</strong> — link each exam to the
                  wings, classes, and sections that will sit the exam.
                </li>
                <li>
                  <strong>Result Cards</strong> — define how source exams combine,
                  weightage, grade scale, and print title (
                  <Link to="/ExaminationSystem/ManageResultCards">
                    Manage Result Cards
                  </Link>
                  ).
                </li>
                <li>
                  <strong>Data Entry</strong> — enter marks once exam classes are
                  configured and the exam is active.
                </li>
              </ol>

              <h5 className="mt-4">2. Mark Entry</h5>
              <p>
                Use <Link to="/ExaminationSystem/DataEntry">Data Entry</Link> to
                record subject marks for each exam. Print entry sheets first if
                you need paper-based collection. Marks must be complete for every
                source exam on the result card before publishing.
              </p>
              <ul>
                <li>Verify the correct exam, wing, class, and section.</li>
                <li>Save entries section by section; review totals before closing the exam.</li>
                <li>Closed or finished exams should not receive further mark changes unless reopened by an administrator.</li>
              </ul>

              <h5 className="mt-4">3. Result Cards — Single vs Composite</h5>
              <p>
                Result cards define how exam marks are combined and presented on
                published reports.
              </p>
              <ul>
                <li>
                  <strong>Single</strong> — one source exam drives the result.
                  Suitable for a standalone term test or annual exam.
                </li>
                <li>
                  <strong>Composite</strong> — multiple source exams are combined
                  using weightage rules. Use this for final results built from
                  first term, second term, and annual components.
                </li>
              </ul>
              <p>
                Create and manage cards from{" "}
                <Link to="/ExaminationSystem/ManageResultCards">
                  Manage Result Cards
                </Link>
                . Each card belongs to one academic session.
              </p>

              <h6 className="mt-3">Editing a result card</h6>
              <p>
                When you save changes to an existing result card (add/remove a source
                exam, change weightage, grade scale, or print title), the system:
              </p>
              <ul>
                <li>
                  <strong>Source exams</strong> — the active list is replaced on save.
                  Older exam links remain in <em>tblResultCardExam</em> with{" "}
                  <code>Is_Deleted = 1</code> for history; only non-deleted rows are
                  used by the application.
                </li>
                <li>
                  <strong>Published snapshots</strong> — if results were published before,
                  all snapshot rows (<em>tblResultData</em>, subject rows, exam breakdown)
                  for that card are cleared and the card is marked unpublished. You must
                  run <strong>Publish Results</strong> again before printing or student
                  portal display.
                </li>
                <li>
                  <strong>Subject rows in the database</strong> — after publish, each
                  student has per-exam subject lines (<code>Source_Exam_ID</code> set)
                  plus combined lines (<code>Source_Exam_ID</code> is null). Null is
                  intentional for combined totals; reports use combined rows when present.
                  Do not treat null as a data error.
                </li>
              </ul>
              <p className="text-muted small mb-0">
                Tip: verify with the Composite Master Sheet after editing, then publish
                the full scope (wing/class/section) you need.
              </p>

              <h5 className="mt-4">4. Weightage Modes</h5>
              <p>Each component (source exam) on a result card supports:</p>
              <ul>
                <li>
                  <strong>PercentOfExam</strong> — the student&apos;s percentage in
                  that exam is multiplied by the component weightage. Example: 80%
                  in an exam with 30% weight contributes 24 points to the composite
                  total.
                </li>
                <li>
                  <strong>SimpleSum</strong> — raw obtained marks from the exam are
                  added directly (subject to the weightage percent setting). Use when
                  components should accumulate marks rather than normalized
                  percentages.
                </li>
                <li>
                  <strong>Include In Total</strong> — uncheck for display-only
                  components that should appear on the card but not affect the
                  overall percentage or grade.
                </li>
              </ul>

              <h5 className="mt-4">5. Grade Scale Types</h5>
              <ul>
                <li>
                  <strong>Standard</strong> — grading thresholds for single-exam
                  style results (A+ from 80%, A from 70%, etc.).
                </li>
                <li>
                  <strong>Combined</strong> — wider composite scale (A+ from 90%,
                  additional E grade band) suited to weighted final results.
                </li>
              </ul>
              <p>
                Print the grade scale and weightage table from{" "}
                <Link to="/ExaminationSystem/ExamDataSO">Print Master Sheet</Link>{" "}
                using <strong>Marks Criteria &amp; Grade Scale</strong>.
              </p>

              <h5 className="mt-4">6. Master Sheet Verification (before publish)</h5>
              <p>
                Before publishing, verify marks from{" "}
                <Link to="/ExaminationSystem/ExamDataSO">Print Master Sheet</Link>:
              </p>
              <ol>
                <li>Select the <strong>session</strong> and <strong>result card</strong>.</li>
                <li>Select <strong>wing</strong>, <strong>class</strong>, and optionally <strong>section</strong>.</li>
                <li>
                  Print <strong>Component Marks Sheets</strong> — raw marks per source
                  exam to confirm data entry is complete. Rows with missing marks are
                  highlighted.
                </li>
                <li>
                  Print <strong>Composite Master Sheet</strong> — combined marks,
                  grades, and section positions using the <em>same calculation engine</em> as
                  publish. Review this carefully; what you see here is what publish will produce.
                </li>
                <li>
                  Print <strong>Marks Criteria &amp; Grade Scale</strong> for examiners
                  and record-keeping.
                </li>
                <li>Fix any missing or incorrect marks in Data Entry, then re-print.</li>
              </ol>

              <h5 className="mt-4">7. Publish Workflow</h5>
              <p>
                Publishing freezes calculated results for students in the selected
                scope. Use{" "}
                <Link to="/ExaminationSystem/PublishResultCard">
                  Publish Results
                </Link>
                :
              </p>
              <ol>
                <li>Complete master sheet verification (step 6 above).</li>
                <li>Select the result card and academic session.</li>
                <li>Choose wing, class, and section (or leave as &quot;All&quot; for bulk publish).</li>
                <li>Click <strong>Publish Results</strong>. The system calculates totals, grades, and section positions.</li>
                <li>
                  Open{" "}
                  <Link to="/ExaminationSystem/PrintPublishedResultCard">
                    Print Published Results
                  </Link>{" "}
                  to print student result cards and class lists from the published snapshot.
                </li>
              </ol>

              <h5 className="mt-4">8. Promotion-Safe Snapshots</h5>
              <p>
                When results are published, the system stores a snapshot in
                dedicated result tables (<em>tblResultData</em>, subject rows, and
                exam breakdown). These snapshots capture student name,
                registration number, class, section, marks, grade, and position at
                publish time.
              </p>
              <ul>
                <li>Snapshots remain valid even after students are promoted to a new class.</li>
                <li>Re-publishing the same card and section replaces the previous snapshot for that scope and increments the publish version.</li>
                <li>Editing a result card (section 3) clears all published snapshots for that card until you publish again.</li>
                <li>Historical published data is never overwritten by live mark-entry tables.</li>
              </ul>

              <h5 className="mt-4">9. Student Portal</h5>
              <p>
                After publishing, students can view their results from the student
                panel under <strong>Exam Results</strong> (<code>/student-panel/exam-results</code>).
              </p>
              <ul>
                <li>
                  <strong>My Results</strong> — list of all published result cards with
                  overall marks, grade, section position, subject-wise detail, and
                  composite exam breakdown.
                </li>
                <li>
                  <strong>Subject Progress</strong> — charts and history showing how the
                  student performed in each subject across published result cards over
                  time. Select a subject to view its trend line, or use the overview to
                  compare subjects on the latest published result.
                </li>
              </ul>

              <h5 className="mt-4">10. Reprint After Promotion</h5>
              <p>
                To reprint a result card after promotion:
              </p>
              <ol>
                <li>Go to Print Published Results and select the original result card.</li>
                <li>Filter by the class and section that existed at publish time if needed.</li>
                <li>
                  The printed report uses snapshot data, so the student&apos;s name,
                  marks, and position reflect the published record—not their
                  current class placement.
                </li>
              </ol>
              <p className="mb-0 text-muted">
                For questions about exam configuration or publishing errors, contact
                the examination office before re-publishing to avoid duplicate
                versions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ExaminationGuidelines;
